import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { convertFromRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import './style.css';
import { Container, Row, Col, Spinner, Alert } from 'react-bootstrap';
import ArticleDetailBanner from './ArticleDetailBanner';
import Card from '../ArticleList/Card';
import PopupRegister from '../Signup/PopupRegister';
import SEOHead from './SEOHead';
import { request } from '../utils';

function ArticleDetail({ user }) {
  const { articlename } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    // Scroll to top when component mounts or article changes
    window.scrollTo(0, 0);
    
    // Check if user is logged in, if not show signup modal
    if (!user?.login) {
      setShowModal(true);
      setLoading(false);
      return;
    }
     else if (user?.login && !user?.email_verified) {
			setShowModal(true);
		}
    
    fetchArticle();
  }, [articlename, user]);

  const fetchArticle = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch the main article by slug
      const response = await request({}, `/public/articles/${articlename}`, 'GET');
      
      if (response.data.status) {
        const articleData = response.data.data;
        setArticle(articleData);
        
        // Fetch related articles from the same category
        if (articleData.category) {
          fetchRelatedArticles(articleData.category, articleData.id);
        }
      } else {
        setError('Article not found');
      }
    } catch (err) {
      console.error('Error fetching article:', err);
      setError('Failed to load article. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedArticles = async (category, currentArticleId) => {
    try {
      const response = await request({}, '/public/articles', 'GET', null, {
        params: {
          category,
          limit: 6
        }
      });
      
      if (response.data.status) {
        // Filter out the current article and limit to 6
        const filtered = response.data.data.articles
          .filter(a => a.id !== currentArticleId)
          .slice(0, 6);
        setRelatedArticles(filtered);
      }
    } catch (err) {
      console.error('Error fetching related articles:', err);
    }
  };

  const renderContent = (content) => {
    if (!content) return '';
    
    try {
      // Try to parse as Draft.js content
      const contentState = JSON.parse(content);
      return draftToHtml(contentState);
    } catch (e) {
      // If parsing fails, assume it's already HTML
      return content;
    }
  };

  // Show signup modal if user is not logged in
  if (!user?.login) {
    const currentReturnUrl = window.location.pathname + window.location.search;
    return (
      <div className="article-detail-page">
        <PopupRegister showModal={showModal} setShowModal={setShowModal} returnUrl={currentReturnUrl} />
        <Container className="py-5">
          <div className="article-auth-alert">
            <div className="article-auth-alert__icon">🔒</div>
            <h2 className="article-auth-alert__heading">Premium Content</h2>
            <p className="article-auth-alert__text">
              Sign up for free to unlock this article and gain access to exclusive injury insights, player analysis, and expert commentary.
            </p>
              <div className="article-auth-alert__buttons">
              <button className="al-button al-button--cta" onClick={() => setShowModal(true)}>
                Login or Free Sign Up to Read More
              </button>
              <button className="al-button al-button--outline" onClick={() => navigate('/articles')}>
                Back to Articles
              </button>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="article-detail-page">
        <Container className="text-center py-5">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">Loading article...</p>
        </Container>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="article-detail-page">
        <Container className="py-5">
          <Alert variant="danger">
            <Alert.Heading>Error</Alert.Heading>
            <p>{error || 'Article not found'}</p>
            <button className="al-button" onClick={() => navigate('/articles')}>
              Back to Articles
            </button>
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div className="article-detail-page">
      <SEOHead article={article} />
      <PopupRegister showModal={showModal} setShowModal={setShowModal} returnUrl={window.location.pathname + window.location.search} />
      {/* Article Detail Banner with Title, Author and Publish Date */}
      <ArticleDetailBanner 
        title={article.title}
        author="Josh Irons"
        publishDate={article.publish_date}
        thumbnail={article.thumbnail}
      />
      
      {/* Main Content Section */}
      <section className="article-main-content">
        <Container>
          <Row className="justify-content-center">
            <Col lg={12}>
              {/* Tag Section */}
              {(article.category || (article.tags && article.tags.length > 0)) && (
                <div className="article-tags">
                  {article.category && (
                    <span className="tag tag-category">{article.category}</span>
                  )}
                  {article.tags && article.tags.map((tag, index) => (
                    <span key={index} className="tag tag-regular">{tag}</span>
                  ))}
                </div>
              )}
              
              <div className="article-header">
                <h1>{article.title}</h1>
                {article.meta_desc && (
                  <p className="lead">{article.meta_desc}</p>
                )}
              </div>
              
              {/* Article Thumbnail */}
              {article.thumbnail && (
                <div className="article-thumbnail">
                  <img src={article.thumbnail} alt={article.title} />
                </div>
              )}
              
              {/* Article Content */}
              <div 
                className="article-body" 
                dangerouslySetInnerHTML={{ __html: renderContent(article.content) }} 
              />
              
              {/* Related Articles Section */}
              {relatedArticles.length > 0 && (
                <div className="related-articles">
                  <h2>See Related Articles</h2>
                  <div className="related-articles-grid">
                    {relatedArticles.map(relatedArticle => (
                      <div key={relatedArticle.id} className="related-article-item">
                        <Card 
                          id={relatedArticle.id}
                          slug={relatedArticle.slug}
                          title={relatedArticle.title}
                          excerpt={relatedArticle.meta_desc || 'Read more about this article...'}
                          image={relatedArticle.thumbnail}
                          user={user}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
}

const mapStateToProps = (state) => ({
	user: state.user
});

export default connect(mapStateToProps)(ArticleDetail);
